import { defineMessages, type IntlShape, useIntl } from 'react-intl';

import { fg } from '@atlaskit/platform-feature-flags';

import { getProductForTranslation } from '../services/get-product-translation-key';
import { isEligibleForStorageHardLimit } from '../services/is-eligible-for-storage-hard-limit';
import type { Product } from '../types';

import { type EditionAwarenessContextType, useEditionAwareness } from './edition-awareness-context';
import { useGetExperimentValue } from './experiment-context';
import { isEligibleForAlignEditionButtonAds } from './is-eligible-for-align-edition-button-ads';
import { isEligibleForConfluenceBuyReverseTrialExperiment } from './is-eligible-for-confluence-buy-reverse-trials';
import { isEligibleForJsmStorageNearLimit } from './is-eligible-for-jsm-storage-near-limit';
import { isEligibleForJsmStorageOverLimit } from './is-eligible-for-jsm-storage-over-limit';
import {
	isEligibleForStandardToPremium,
	isEligibleForStandardToPremiumUpsell,
} from './is-eligible-for-standard-to-premium';
import { isProductOneOf } from './is-product-one-of';

const buttonMessages = defineMessages({
	upgradeText: {
		id: '@atlassian/edition-awareness.button.upgrade',
		defaultMessage: 'Upgrade',
		description: 'Informs the user that they can upgrade their product',
	},
	addPayment: {
		id: '@atlassian/edition-awareness.button.add-payment',
		defaultMessage: 'Add payment',
		description: 'Informs the user that they need to add a payment method',
	},
	tooltipUpgradeYourPlan: {
		id: '@atlassian/edition-awareness.button.tooltip-upgrade-your-plan',
		defaultMessage: 'Upgrade your plan',
		description: 'Informs the user that they can upgrade their app',
	},
	tooltipConfirmYourPlan: {
		id: '@atlassian/edition-awareness.button.tooltip-confirm-your-plan',
		defaultMessage: 'Confirm your plan',
		description:
			'Informs the user that they need to confirm whether to keep the plan they are trialling',
	},
	tooltipConfirmAndPay: {
		id: '@atlassian/edition-awareness.button.tooltip-confirm-and-pay',
		defaultMessage: 'Confirm and pay',
		description:
			'Informs the user that they need to add payment details for the plan they are trialling',
	},
	tooltipKeepYourPlan: {
		id: '@atlassian/edition-awareness.button.tooltip-keep-your-plan',
		defaultMessage: 'Keep your plan',
		description:
			'Informs the user that they need to add payment details to keep the plan they are on',
	},
	premiumTrial: {
		id: '@atlassian/edition-awareness.button.premium-trial',
		defaultMessage: 'Premium trial',
		description: 'Informs the user that they are on a Premium trial',
	},
	standardTrial: {
		id: '@atlassian/edition-awareness.button.standard-trial',
		defaultMessage: 'Standard trial',
		description: 'Informs the user that they are on a Standard trial',
	},
	trialDaysLeft: {
		id: '@atlassian/edition-awareness.button.trial-days-left',
		defaultMessage: '{days}d',
		description: 'Informs the user how many days they have left in trial',
	},
	storageNearLimit: {
		id: '@atlassian/edition-awareness.button.storage-near-limit',
		defaultMessage: 'Near limit',
		description:
			'Shown on the edition awareness pill when JSM Free storage usage is approaching the limit (near-limit threshold).',
	},
});

// TODO: `licensedUserLimit` should be fetched from the server
const PRODUCT_LIMITS_MAP: Record<Product, { licensedUserLimit: number; warningUserLimit: number }> =
	{
		'jira-product-discovery': {
			licensedUserLimit: 3,
			warningUserLimit: 2,
		},
		jira: {
			licensedUserLimit: 10,
			warningUserLimit: 7,
		},
		confluence: {
			licensedUserLimit: 10,
			warningUserLimit: 3,
		},
		'jira-service-management': {
			licensedUserLimit: 3,
			warningUserLimit: 2,
		},
		'jira-customer-service': {
			licensedUserLimit: 3,
			warningUserLimit: 2,
		},
	};

export type IconType = 'gem' | 'warning' | 'credit-card' | 'arrow-up-circle';
export type Appearance = 'default' | 'warning' | 'danger' | 'discovery';

export type ButtonContent = {
	appearance: Appearance;
	badge?: string;
	icon?: IconType;
	isDismissible?: boolean;
	text: string;
	tooltip?: string;
};

function getFreeContent({
	context,
	formatMessage,
	getExperimentValue,
	isEligibleForAds,
}: {
	context: EditionAwarenessContextType;
	formatMessage: IntlShape['formatMessage'];
	getExperimentValue: ReturnType<typeof useGetExperimentValue>['getExperimentValue'];
	isEligibleForAds: boolean;
}): ButtonContent {
	const { licensedUsers, product, automationFreeLimitStatus } = context;
	const limits = PRODUCT_LIMITS_MAP[product];

	const upgradeText = formatMessage(buttonMessages.upgradeText);

	if (isEligibleForAds) {
		if (automationFreeLimitStatus) {
			if (
				automationFreeLimitStatus === 'BREACHED' &&
				getExperimentValue({
					statsigParams: {
						experimentName: 'twcg-1076_jira_automation_limits_experiment',
						parameterName: 'cohort',
						defaultValue: 'not-enrolled',
					},
				}) === 'variation'
			) {
				return {
					text: upgradeText,
					appearance: 'warning',
					icon: 'warning',
					tooltip: formatMessage({
						id: '@atlassian/edition-awareness.button.tooltip-automation-limit-reached',
						defaultMessage: 'Automation limit reached',
						description: 'Informs the user that they have breached their automation limit',
					}),
				};
			}
			if (
				automationFreeLimitStatus === 'PROJECTED_TO_BREACH' &&
				getExperimentValue({
					statsigParams: {
						experimentName: 'twcg-1076_jira_automation_limits_experiment',
						parameterName: 'cohort',
						defaultValue: 'not-enrolled',
					},
				}) === 'variation'
			) {
				return {
					text: upgradeText,
					appearance: 'warning',
					icon: 'arrow-up-circle',
					tooltip: formatMessage({
						id: '@atlassian/edition-awareness.button.tooltip-automation-limit-almost-reached',
						defaultMessage: 'Automation limit almost reached',
						description: 'Informs the user that they are about to breach their automation limit',
					}),
				};
			}
		}

		if (isEligibleForStorageHardLimit(context)) {
			return {
				text: upgradeText,
				appearance: 'warning',
				icon: 'warning',
				tooltip: formatMessage({
					id: '@atlassian/edition-awareness.button.tooltip-storage-limit-reached',
					defaultMessage: 'Storage limit reached',
					description: 'Informs the user that they have breached their storage limit',
				}),
			};
		}

		if (licensedUsers >= limits.licensedUserLimit) {
			return {
				text: upgradeText,
				appearance: 'warning',
				icon: 'warning',
				tooltip: formatMessage(
					{
						id: '@atlassian/edition-awareness.button.tooltip-user-limit-reached',
						defaultMessage:
							'{product, select, jpd {Creator} jsm {Agent} jcs {Agent} other {User}} limit reached',
						description:
							'Informs the user that there are no licensed users left and to upgrade their app',
					},
					{ product: getProductForTranslation(product) },
				),
			};
		}

		if (licensedUsers >= limits.warningUserLimit) {
			return {
				text: upgradeText,
				appearance: 'warning',
				icon: 'arrow-up-circle',
				tooltip: formatMessage(
					{
						id: '@atlassian/edition-awareness.button.tooltip-user-limit-almost-reached',
						defaultMessage:
							'{product, select, jpd {Creator} jsm {Agent} jcs {Agent} other {User}} limit almost reached',
						description:
							'Informs the user that there are limited licensed users left before they need to upgrade their app',
					},
					{ product: getProductForTranslation(product) },
				),
			};
		}

		return {
			text: upgradeText,
			appearance: 'discovery',
			icon: 'arrow-up-circle',
			tooltip: formatMessage(buttonMessages.tooltipUpgradeYourPlan),
		};
	}

	if (automationFreeLimitStatus) {
		if (
			automationFreeLimitStatus === 'BREACHED' &&
			getExperimentValue({
				statsigParams: {
					experimentName: 'twcg-1076_jira_automation_limits_experiment',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
			}) === 'variation'
		) {
			return {
				text: formatMessage({
					id: '@atlassian/edition-awareness.button.automation-limits-breached',
					defaultMessage: 'Limit reached',
					description: 'Informs the user that they have breached their automation limits',
				}),
				appearance: 'danger',
				icon: 'warning',
			};
		}
		if (
			automationFreeLimitStatus === 'PROJECTED_TO_BREACH' &&
			getExperimentValue({
				statsigParams: {
					experimentName: 'twcg-1076_jira_automation_limits_experiment',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
			}) === 'variation'
		) {
			return {
				text: formatMessage({
					id: '@atlassian/edition-awareness.button.automation-near-limit',
					defaultMessage: 'Near limit',
					description: 'Informs the user that they are about to breach their automation limits',
				}),
				appearance: 'warning',
				icon: 'warning',
			};
		}
	}

	if (isEligibleForStorageHardLimit(context) || isEligibleForJsmStorageOverLimit(context)) {
		return {
			text: formatMessage({
				id: '@atlassian/edition-awareness.button.storage-hard-limit',
				defaultMessage: 'Storage full',
				description:
					'Shown on the edition awareness pill when storage usage is at or over the limit.',
			}),
			appearance: 'warning',
			icon: 'warning',
		};
	}

	if (isProductOneOf(product, ['jira', 'jira-service-management', 'jira-product-discovery'])) {
		if (licensedUsers >= limits.licensedUserLimit) {
			return {
				text: formatMessage(
					{
						id: '@atlassian/edition-awareness.button.no-users-left',
						defaultMessage:
							'No {product, select, jpd {creators} jsm {agents} jcs {agents} jira {users} confluence {users} other {users}} left',
						description:
							'Informs the user that there are no licensed users left and they may need to upgrade their product',
					},
					{ product: getProductForTranslation(product) },
				),
				appearance: product !== 'confluence' ? 'danger' : 'warning',
				icon: 'warning',
			};
		}

		if (licensedUsers >= limits.warningUserLimit) {
			return {
				text: formatMessage(
					{
						id: '@atlassian/edition-awareness.button.limited-users-left',
						defaultMessage:
							'Limited {product, select, jpd {creators} jsm {agents} jcs {agents} jira {users} confluence {users} other {users}} left',
						description:
							'Informs the user that there are limited licensed users left before they need to upgrade their product',
					},
					{ product: getProductForTranslation(product) },
				),
				appearance: 'warning',
				icon: 'warning',
			};
		}
	}

	if (isEligibleForJsmStorageNearLimit(context)) {
		return {
			text: formatMessage(buttonMessages.storageNearLimit),
			appearance: 'warning',
			icon: 'warning',
		};
	}

	if (product === 'confluence' && licensedUsers >= limits.warningUserLimit) {
		return {
			text: upgradeText,
			appearance: 'warning',
			icon: 'warning',
		};
	}

	return {
		text: upgradeText,
		appearance: 'default',
		icon: 'gem',
	};
}

function getTrialContent({
	context,
	formatMessage,
	getExperimentValue,
	isEligibleForAds,
}: {
	context: EditionAwarenessContextType;
	formatMessage: IntlShape['formatMessage'];
	getExperimentValue: ReturnType<typeof useGetExperimentValue>['getExperimentValue'];
	isEligibleForAds: boolean;
}): ButtonContent {
	const { product, edition, trialDaysLeft, trialLengthDays, isInReverseTrial, userRoles } = context;

	const isInFirstHalfOfTrial = Boolean(
		trialDaysLeft && trialLengthDays && trialDaysLeft > Math.ceil(trialLengthDays / 2),
	);

	const addPaymentText = formatMessage(buttonMessages.addPayment);
	const confirmYourPlanTooltip = formatMessage(buttonMessages.tooltipConfirmYourPlan);
	const confirmAndPayTooltip = formatMessage(buttonMessages.tooltipConfirmAndPay);
	const premiumTrialText = formatMessage(buttonMessages.premiumTrial);
	const standardTrialText = formatMessage(buttonMessages.standardTrial);

	const badge =
		trialDaysLeft !== undefined
			? formatMessage(buttonMessages.trialDaysLeft, { days: trialDaysLeft })
			: undefined;

	if (isEligibleForAds) {
		if (isInFirstHalfOfTrial) {
			return {
				text: edition === 'premium' ? premiumTrialText : standardTrialText,
				appearance: 'default',
				icon: undefined,
				tooltip: confirmYourPlanTooltip,
				badge,
			};
		}

		if (trialDaysLeft !== undefined && trialDaysLeft <= 3) {
			return {
				text: addPaymentText,
				appearance: 'warning',
				icon: 'credit-card',
				tooltip: confirmAndPayTooltip,
			};
		}

		return {
			text: edition === 'premium' ? premiumTrialText : standardTrialText,
			appearance: 'default',
			icon: undefined,
			tooltip: confirmYourPlanTooltip,
			badge,
		};
	}

	// Control cohort / not eligible for ads
	let appearance: ButtonContent['appearance'] = 'default';
	let icon: ButtonContent['icon'] = 'gem';

	const daysLeftText = formatMessage(
		{
			id: '@atlassian/edition-awareness.button.daysLeft',
			defaultMessage: '{trialDaysLeft, plural, one {# day} other {# days}} left',
			description: 'Informs the user how many days they have left in trial',
		},
		{ trialDaysLeft },
	);

	if (
		edition === 'premium' &&
		product === 'jira' &&
		isInReverseTrial &&
		userRoles?.includes('BILLING_ADMIN')
	) {
		if (
			getExperimentValue({
				statsigParams: {
					experimentName: 'jira_reverse_trial_ea_updates_during_trial',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
			}) === 'variation'
		) {
			appearance = 'default';
			icon = 'gem';

			if (isInFirstHalfOfTrial) {
				return {
					text: formatMessage({
						id: '@atlassian/edition-awareness.button.see-plans',
						defaultMessage: 'See plans',
						description: 'Informs the user that they are able to see plans',
					}),
					appearance,
					icon,
				};
			}

			if (trialDaysLeft && trialDaysLeft <= 3) {
				appearance = 'warning';
				icon = 'warning';
			}

			return {
				text: daysLeftText,
				appearance,
				icon,
			};
		}
	}

	if (isProductOneOf(product, ['jira', 'jira-service-management', 'jira-product-discovery'])) {
		// If trial is still in its first half, show trial message instead of trial days left
		if (isInFirstHalfOfTrial) {
			if (edition === 'premium') {
				return {
					text: premiumTrialText,
					appearance,
					icon,
				};
			}

			if (edition === 'standard') {
				return {
					text: standardTrialText,
					appearance,
					icon,
				};
			}
		}

		// If trial has 3 days or less remaining, swap the button to the warning state
		if (trialDaysLeft && trialDaysLeft <= 3) {
			appearance = 'warning';
			icon = 'warning';
		}

		return {
			text: daysLeftText,
			appearance,
			icon,
		};
	}

	if (
		isEligibleForConfluenceBuyReverseTrialExperiment(context) &&
		fg('growth_confluence_buy_reverse_trials_fg')
	) {
		const abCohort = getExperimentValue({
			statsigParams: {
				experimentName: 'growth_confluence_buy_reverse_trials_ab',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
			},
			trackEventParameters: {
				trackEventGrowthInitiative: 'luna',
				trackEventAttributes: {
					touchpointId: 'use-button-content getTrialContent',
				},
			},
		});

		if (abCohort === 'variation') {
			appearance = 'default';
			icon = 'gem';

			if (trialDaysLeft && trialDaysLeft > 15) {
				return {
					text: formatMessage({
						id: '@atlassian/edition-awareness.button.see-plans.cbrt',
						defaultMessage: 'See plans',
						description: 'Informs the user that they are able to see plans',
					}),
					appearance,
					icon,
				};
			}

			if (trialDaysLeft && trialDaysLeft <= 3) {
				appearance = 'warning';
				icon = 'warning';
			}

			return {
				text: formatMessage(
					{
						id: '@atlassian/edition-awareness.button.see-plans.cbrt.final',
						// eslint-disable-next-line @atlassian/i18n/enforce-plural-rules
						defaultMessage: '{trialDaysLeft, plural, =1 {# day} other {# days}} left',
						description: 'Indicates to the user how many days they have left in trial',
					},
					{ trialDaysLeft },
				),
				appearance,
				icon,
			};
		}
	}

	return {
		text: daysLeftText,
		appearance,
		icon,
	};
}

function getPredunningContent({
	context,
	formatMessage,
	getExperimentValue,
	isEligibleForAds,
}: {
	context: EditionAwarenessContextType;
	formatMessage: IntlShape['formatMessage'];
	getExperimentValue: ReturnType<typeof useGetExperimentValue>['getExperimentValue'];
	isEligibleForAds: boolean;
}): ButtonContent {
	const { edition, product, isInReverseTrial, userRoles } = context;

	const addPaymentText = formatMessage(buttonMessages.addPayment);
	const keepYourPlanTooltip = formatMessage(buttonMessages.tooltipKeepYourPlan);

	if (isEligibleForAds) {
		return {
			text: addPaymentText,
			appearance: 'warning',
			icon: 'credit-card',
			tooltip: keepYourPlanTooltip,
		};
	}

	// Control cohort / not eligible for ads
	let appearance: ButtonContent['appearance'] = 'default';
	let icon: ButtonContent['icon'] = 'credit-card';

	if (
		product === 'jira' &&
		edition === 'premium' &&
		isInReverseTrial &&
		userRoles?.includes('BILLING_ADMIN')
	) {
		if (
			getExperimentValue({
				statsigParams: {
					experimentName: 'jira_reverse_trial_ea_updates_during_trial',
					parameterName: 'cohort',
					defaultValue: 'not-enrolled',
				},
			}) === 'variation'
		) {
			return {
				text: formatMessage({
					id: '@atlassian/edition-awareness.button.choose-plans',
					defaultMessage: 'Choose a plan',
					description: 'Informs the user that they are able to choose a plan',
				}),
				appearance: 'warning',
				icon: 'warning',
			};
		}
	}

	if (
		isEligibleForConfluenceBuyReverseTrialExperiment(context) &&
		fg('growth_confluence_buy_reverse_trials_fg')
	) {
		const abCohort = getExperimentValue({
			statsigParams: {
				experimentName: 'growth_confluence_buy_reverse_trials_ab',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
			},
			trackEventParameters: {
				trackEventGrowthInitiative: 'luna',
				trackEventAttributes: {
					touchpointId: 'use-button-content getPredunningContent',
				},
			},
		});

		if (abCohort === 'variation') {
			return {
				text: formatMessage({
					id: '@atlassian/edition-awareness.button.see-plans.final',
					defaultMessage: 'Choose a plan',
					description: 'Informs the user that they are able to choose a plan',
				}),
				appearance: 'warning',
				icon: 'warning',
			};
		}
	}

	return {
		text: formatMessage({
			id: '@atlassian/edition-awareness.button.addPaymentDetails',
			defaultMessage: 'Add payment details',
			description: 'Informs the user how many days they have left in trial',
		}),
		appearance,
		icon,
	};
}

function getCollectionContent({
	context,
	formatMessage,
	isEligibleForAds,
}: {
	context: EditionAwarenessContextType;
	formatMessage: IntlShape['formatMessage'];
	isEligibleForAds: boolean;
}): ButtonContent {
	/* ==============================
	// Collection
	// Collection handles its own logic for the button content
	// This is to avoid the button content from being overridden by other products' logic
	// ============================== */

	const {
		isInTrial,
		trialDaysLeft,
		isInPredunning,
		invoiceableStatus,
		canAddPaymentDetails,
		edition,
	} = context;

	const upgradeText = formatMessage(buttonMessages.upgradeText);
	const addPaymentText = formatMessage(buttonMessages.addPayment);
	const confirmYourPlanTooltip = formatMessage(buttonMessages.tooltipConfirmYourPlan);
	const confirmAndPayTooltip = formatMessage(buttonMessages.tooltipConfirmAndPay);
	const keepYourPlanTooltip = formatMessage(buttonMessages.tooltipKeepYourPlan);
	const premiumTrialText = formatMessage(buttonMessages.premiumTrial);
	const standardTrialText = formatMessage(buttonMessages.standardTrial);

	if (isEligibleForAds) {
		if (isInTrial) {
			const badge =
				trialDaysLeft !== undefined
					? formatMessage(buttonMessages.trialDaysLeft, { days: trialDaysLeft })
					: undefined;

			if (trialDaysLeft !== undefined && trialDaysLeft <= 3 && canAddPaymentDetails) {
				return {
					text: addPaymentText,
					appearance: 'warning',
					icon: 'credit-card',
					tooltip: confirmAndPayTooltip,
				};
			}

			return {
				text: edition === 'premium' ? premiumTrialText : standardTrialText,
				appearance: 'default',
				icon: undefined,
				tooltip: confirmYourPlanTooltip,
				badge,
			};
		}

		if (isInPredunning && canAddPaymentDetails) {
			return {
				text: addPaymentText,
				appearance: 'warning',
				icon: 'credit-card',
				tooltip: keepYourPlanTooltip,
			};
		}

		return {
			text: upgradeText,
			appearance: 'discovery',
			icon: 'arrow-up-circle',
			tooltip: formatMessage(buttonMessages.tooltipUpgradeYourPlan),
		};
	}

	// Control cohort / not eligible for ads
	if (isInTrial) {
		let appearance: ButtonContent['appearance'] = 'default';
		let icon: ButtonContent['icon'] = 'gem';

		const daysLeftText = formatMessage(
			{
				id: '@atlassian/edition-awareness.button.collection.daysLeft',
				defaultMessage: '{trialDaysLeft, plural, one {# day} other {# days}} left',
				description: 'Informs the user how many days they have left in trial',
			},
			{ trialDaysLeft },
		);

		if (trialDaysLeft && trialDaysLeft <= 3) {
			if (invoiceableStatus === 'NOT_INVOICEABLE' && canAddPaymentDetails) {
				appearance = 'warning';
				icon = 'warning';
			}
		}

		return {
			text: daysLeftText,
			appearance,
			icon,
		};
	}

	if (isInPredunning) {
		if (canAddPaymentDetails && invoiceableStatus === 'NOT_INVOICEABLE') {
			return {
				text: formatMessage({
					id: '@atlassian/edition-awareness.button.collection.addPaymentDetails',
					defaultMessage: 'Add payment details',
				}),
				appearance: 'default',
				icon: 'gem',
			};
		}
	}

	return {
		text: upgradeText,
		appearance: 'default',
		icon: 'gem',
	};
}

export function useButtonContent(): ButtonContent | null {
	const { formatMessage } = useIntl();

	const context = useEditionAwareness();
	const { getExperimentValue } = useGetExperimentValue();

	const { edition, isInPredunning, isInTrial, isInCollection, product } = context;

	const isEligibleForAds = Boolean(
		isEligibleForAlignEditionButtonAds(context) &&
		getExperimentValue({
			statsigParams: {
				experimentName: 'align_edition_button_ads',
				parameterName: 'cohort',
				defaultValue: 'not-enrolled',
				options: {
					fireExperimentExposure: false,
				},
			},
		}) === 'variation',
	);

	/*
	 * Keep this before the other checks to avoid showing the collection content in the other cases
	 */
	if (isInCollection && isProductOneOf(product, ['jira', 'confluence'])) {
		return getCollectionContent({ context, formatMessage, isEligibleForAds });
	}

	if (edition === 'free') {
		return getFreeContent({ context, formatMessage, getExperimentValue, isEligibleForAds });
	}

	if (isInTrial) {
		return getTrialContent({ context, formatMessage, getExperimentValue, isEligibleForAds });
	}

	if (isInPredunning) {
		return getPredunningContent({ context, formatMessage, getExperimentValue, isEligibleForAds });
	}

	if (isEligibleForStandardToPremiumUpsell(context) || isEligibleForStandardToPremium(context)) {
		return {
			text: formatMessage({
				id: '@atlassian/edition-awareness.button.upgrade.standard-to-premium',
				defaultMessage: 'Upgrade',
				description: 'Informs the user that they can upgrade their product',
			}),
			appearance: 'default',
			icon: 'gem',
			isDismissible: true,
		};
	}

	return null;
}
